import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, CalendarPlus } from "lucide-react";
import { API } from "../../context/AuthContext";
import { StatusBadge, PageCite } from "./StatusBadge";

const pill = "flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-1 rounded border transition-colors duration-200";

export function DeadlinesTab({ deadlines, canEdit, onSource, onChanged }) {
  const review = async (d, action) => {
    try {
      await axios.post(`${API}/deadlines/${d.deadline_id}/review`, { action });
      toast.success(action === "approve" ? "Deadline approved and added to the timeline." : "Deadline candidate rejected.");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update the deadline");
    }
  };
  if (!deadlines.length) return <div className="text-xs italic text-gray-500 py-6" data-testid="deadlines-tab-empty">No deadline candidates in this document.</div>;
  return (
    <div className="space-y-2" data-testid="deadlines-tab">
      <p className="text-[11px] text-amber-200/80">Candidates only — nothing is calendared until an attorney approves it.</p>
      {deadlines.map((d) => (
        <div key={d.deadline_id} className="border border-white/5 rounded-lg p-3 bg-gray-950 space-y-1.5" data-testid={`deadline-${d.deadline_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-bold text-[#f59e0b]" data-testid={`deadline-date-${d.deadline_id}`}>{d.candidate_date || "No date — calendar manually"}</span>
            <StatusBadge status={d.review_status} testId={`deadline-status-${d.deadline_id}`} />
            <span className="text-[9px] font-mono uppercase text-gray-500">{d.computation_method.replace(/_/g, " ")}</span>
            <span className="ml-auto"><PageCite page={d.source_ref?.page} quote={d.source_ref?.quote} onOpen={onSource} testId={`deadline-cite-${d.deadline_id}`} /></span>
          </div>
          <div className="text-xs text-gray-200">{d.description}</div>
          {d.assigned_party && <div className="text-[10px] font-mono text-gray-400">Assigned: {d.assigned_party}</div>}
          {d.legal_basis && <div className="text-[10px] font-mono text-gray-400">Basis cited in document: {d.legal_basis}</div>}
          <ul className="text-[10px] font-mono text-gray-400 list-disc pl-4 space-y-0.5">
            {(d.calculation?.steps || []).map((s, i) => <li key={i}>{s}</li>)}
            {(d.calculation?.assumptions || []).map((s, i) => <li key={`a${i}`} className="text-gray-500 italic">{s}</li>)}
          </ul>
          {canEdit && (
            <div className="flex gap-2 pt-1">
              <button disabled={d.review_status === "approved" || !d.candidate_date} onClick={() => review(d, "approve")} data-testid={`deadline-approve-${d.deadline_id}`} className={`${pill} text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10 disabled:opacity-40`}><Check className="w-3 h-3" /> ATTORNEY APPROVE</button>
              <button disabled={d.review_status === "rejected"} onClick={() => review(d, "reject")} data-testid={`deadline-reject-${d.deadline_id}`} className={`${pill} text-rose-300 border-rose-500/30 hover:bg-rose-500/10 disabled:opacity-40`}><X className="w-3 h-3" /> REJECT</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function UndatedList({ facts, canEdit, onSource, onChanged, showDoc }) {
  const [dating, setDating] = useState(null);
  const [date, setDate] = useState("");
  const [why, setWhy] = useState("");
  const resolve = async (f, action) => {
    try {
      await axios.post(`${API}/undated/${f.fact_id}/resolve`, { action, date: action === "date" ? date : null, reason: action === "date" ? why : "Dismissed by reviewer" });
      toast.success(action === "date" ? "Dated and added to the timeline." : "Dismissed.");
      setDating(null); setDate(""); setWhy("");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resolve");
    }
  };
  if (!facts.length) return <div className="text-xs italic text-gray-500 py-4" data-testid="undated-empty">No undated facts.</div>;
  return (
    <div className="space-y-2" data-testid="undated-list">
      {facts.map((f) => (
        <div key={f.fact_id} className="border border-white/5 rounded-lg p-3 bg-gray-950 space-y-1" data-testid={`undated-${f.fact_id}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 text-xs text-gray-200">{f.fact}</div>
            {onSource && <PageCite page={f.page} quote={f.quote} onOpen={(p, q) => onSource(p, q, f.document_id)} testId={`undated-cite-${f.fact_id}`} />}
          </div>
          <div className="text-[10px] font-mono text-gray-500">{f.reason}{f.original_date_text ? ` · as written: “${f.original_date_text}”` : ""}{showDoc && f.document_name ? ` · ${f.document_name}` : ""}</div>
          {canEdit && f.review_status !== "dated" && (dating === f.fact_id ? (
            <div className="flex flex-wrap gap-2 pt-1">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`undated-date-input-${f.fact_id}`} className="bg-gray-950 border border-white/10 rounded px-2 py-1 text-xs text-gray-200" />
              <input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Where does this date come from?" data-testid={`undated-reason-input-${f.fact_id}`} className="flex-1 min-w-[160px] bg-gray-950 border border-white/10 rounded px-2 py-1 text-xs text-gray-200" />
              <button onClick={() => resolve(f, "date")} data-testid={`undated-save-${f.fact_id}`} className={`${pill} text-gray-950 bg-[#f59e0b] border-[#f59e0b]`}>SAVE</button>
            </div>
          ) : (
            <div className="flex gap-2 pt-1">
              <button onClick={() => setDating(f.fact_id)} data-testid={`undated-assign-${f.fact_id}`} className={`${pill} text-[#f59e0b] border-[#f59e0b]/30 hover:bg-[#f59e0b]/10`}><CalendarPlus className="w-3 h-3" /> ASSIGN DATE</button>
              <button onClick={() => resolve(f, "dismiss")} data-testid={`undated-dismiss-${f.fact_id}`} className={`${pill} text-gray-400 border-white/10 hover:text-white`}>DISMISS</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
